import { Card } from "@/components/base/Card";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";
import { computeCycleExamples, chitValue, durationMonths } from "@/utils/group";
import { formatIndianNumber } from "@/utils/format";

interface EconomicsPreviewProps {
  monthlyInstallment: number;
  memberCount: number;
  reservedCycle: number;
}

function Metric({ label, value, highlight }: { label: string; value: React.ReactNode; highlight?: boolean }) {
  return (
    <div className={`min-w-0 rounded-lg border p-2.5 transition-colors ${highlight ? "border-primary-200 bg-primary-50/50" : "border-background-200/80 bg-background-50/90"}`}>
      <p className="truncate text-[11px] font-medium text-foreground-500">{label}</p>
      <div className="tnum mt-1 truncate text-sm font-bold text-foreground-950">{value}</div>
    </div>
  );
}

/**
 * The live economics preview shown persistently in the create-group wizard.
 * Illustrative only — computed in the browser for display. Real cycle figures
 * come from the backend once a group is active. Never a source of truth for money.
 */
export function EconomicsPreview({ monthlyInstallment, memberCount, reservedCycle }: EconomicsPreviewProps) {
  const rows = computeCycleExamples({ monthlyInstallment, memberCount, reservedCycle });
  const pool = chitValue(monthlyInstallment, memberCount);
  const duration = durationMonths(memberCount);

  return (
    <Card
      title={
        <div className="flex items-center justify-between gap-2">
          <span>Live Economics</span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-success-500/10 px-2 py-0.5 text-[11px] font-semibold text-success-700">
            <span className="h-1.5 w-1.5 rounded-full bg-success-600 animate-pulse" />
            Live Preview
          </span>
        </div>
      }
      subtitle="Updates automatically as you tweak details."
      className="border-background-200 bg-background-100/30 shadow-sm"
    >
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-2">
        <Metric label="Chit Value (Pool)" value={<CurrencyDisplay amount={pool} align="left" />} highlight />
        <Metric label="Monthly Installment" value={<CurrencyDisplay amount={monthlyInstallment} align="left" />} />
        <Metric label="Total Members" value={`${formatIndianNumber(memberCount)} members`} />
        <Metric label="Group Duration" value={`${formatIndianNumber(duration)} months`} />
        <Metric label="Reserved Cycle" value={`Cycle ${reservedCycle}`} />
        <Metric label="Total Expected Pool" value={<CurrencyDisplay amount={pool} align="left" />} />
      </div>

      <div className="mt-4 min-w-0 overflow-hidden rounded-lg border border-background-200 bg-background-50">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[340px] border-collapse text-left text-xs">
            <thead>
              <tr className="border-b border-background-200 bg-background-100/70 text-[11px] font-semibold text-foreground-600">
                <th className="px-3 py-2">Cycle</th>
                <th className="px-2.5 py-2 text-right">Prize</th>
                <th className="px-2.5 py-2 text-right">Discount</th>
                <th className="px-2.5 py-2 text-right">Dividend</th>
                <th className="px-3 py-2 text-right">Net Pay</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-100 text-[11.5px]">
              {rows.map((r) => (
                <tr key={r.key} className="hover:bg-background-100/40">
                  <td className="px-3 py-2">
                    <span className="font-semibold text-foreground-900">{r.label}</span>
                    <span className="block text-[10.5px] text-foreground-500">{r.note}</span>
                  </td>
                  <td className="px-2.5 py-2 text-right font-medium">
                    <CurrencyDisplay amount={r.winningBid} hideSymbol />
                  </td>
                  <td className="px-2.5 py-2 text-right text-foreground-600">
                    <CurrencyDisplay amount={r.discount} hideSymbol />
                  </td>
                  <td className="px-2.5 py-2 text-right text-success-600">
                    <CurrencyDisplay amount={r.perMemberDividend} hideSymbol />
                  </td>
                  <td className="px-3 py-2 text-right font-semibold text-foreground-900">
                    <CurrencyDisplay amount={r.netInstallment} hideSymbol />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <p className="mt-3 text-[11px] leading-relaxed text-foreground-500">
        In every cycle the total collected equals the amount paid out. Real figures are finalized upon auction closure.
      </p>
    </Card>
  );
}